import { FormikProvider, useField, useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useErrorBoundary, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { GraphQLErrorMessage, Input, Select, SettingsRow, tokens } from '@treams/treams-ui';

const Dot = styled(Box)<{ $color: string }>`
	background: ${(props) => props.$color};
	width: 10px;
	height: 10px;
	border-radius: 5px;
	margin-top: -3px;
`;

function LearningTypeColorSelector() {
	const { t } = useTranslation();

	const colors = [
		{
			value: '#6DD400',
			label: t('common.settings.learning.learningTypes.colors.green'),
		},
		{
			value: '#F7B500',
			label: t('common.settings.learning.learningTypes.colors.yellow'),
		},
		{
			value: '#E02020',
			label: t('common.settings.learning.learningTypes.colors.red'),
		},
		{
			value: '#32C5FF',
			label: t('common.settings.learning.learningTypes.colors.blue'),
		},
		{
			value: tokens.text.primary,
			label: t('common.settings.learning.learningTypes.colors.anthracite'),
		},
		{
			value: '#3A339F',
			label: t('common.settings.learning.learningTypes.colors.purple'),
		},
	];

	const [field, , { setValue }] = useField('color');

	return (
		<Select
			small
			selectedValue={field.value}
			options={colors.map(({ value, label }) => ({
				title: label,
				value,
			}))}
			onChange={(value) => setValue(value!)}
		/>
	);
}

export default function LearningTypeEdit(props: {
	learningType?: graphql.LearningType;
	onRefresh: () => void;
}) {
	const { language } = useLocalize();
	const formik = useFormik({
		initialValues: {
			translations: props.learningType?.title ?? [
				{
					languageID: language.id,
					text: '',
				},
			],
			color: props.learningType?.color ?? '#4840BE',
		},
		onSubmit: () => {},
	});

	const [editing, setEditing] = useState(!props.learningType);

	const isArchived = !!props.learningType?.isArchived;

	const [error, attempt, reset] = useErrorBoundary();

	const [update, { loading: loadingUpdate }] = graphql.useUpdateLearningTypeMutation();
	const [create, { loading: loadingCreate }] = graphql.useCreateLearningTypeMutation();
	const [archive, { loading: loadingArchive }] = graphql.useArchiveLearningTypeMutation();
	const [restore, { loading: loadingRestore }] = graphql.useRestoreLearningTypeMutation();

	const onEdit = () => {
		setEditing(true);
		reset();
	};

	const onSave = attempt(async () => {
		setEditing(false);

		const title = formik.values.translations
			.filter((translation) => translation.text.length > 0)
			.map(({ languageID, text }) => ({ languageID, text }));

		formik.values.translations = title;

		if (props.learningType) {
			await update({
				variables: {
					input: {
						id: props.learningType.id,
						title,
						color: formik.values.color,
					},
				},
			});
		} else {
			await create({
				variables: {
					input: {
						title,
						color: formik.values.color,
					},
				},
			});
		}

		props.onRefresh();
	});

	const onRestore = attempt(async () => {
		await restore({
			variables: {
				input: {
					id: props.learningType!.id,
				},
			},
		});

		props.onRefresh();
	});

	const onDelete = attempt(async () => {
		await archive({
			variables: {
				input: {
					id: props.learningType!.id,
				},
			},
		});

		props.onRefresh();
	});

	return (
		<SettingsRow
			loading={loadingUpdate || loadingCreate || loadingArchive || loadingRestore}
			onEdit={isArchived || editing ? undefined : onEdit}
			onRestore={isArchived ? onRestore : undefined}
			onSave={editing ? onSave : undefined}
			onDelete={props.learningType && !editing && !isArchived ? onDelete : undefined}
		>
			<GraphQLErrorMessage error={error} />
			{editing ? (
				<FormikProvider value={formik}>
					<Flex>
						<Box flexGrow={1}>
							<Input.Localizable name="translations" />
						</Box>
						<Box ml={3} width={200}>
							<LearningTypeColorSelector />
						</Box>
					</Flex>
				</FormikProvider>
			) : (
				<Flex alignItems="center">
					<Box>
						{
							formik.values.translations
								.filter((translation) => translation.languageID === language.id)
								.concat(formik.values.translations)[0]?.text
						}
					</Box>
					<Dot $color={formik.values.color} ml={2} />
				</Flex>
			)}
		</SettingsRow>
	);
}
